import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Observable,
  of,
  map,
  catchError,
  switchMap,
  from,
  BehaviorSubject,
} from 'rxjs';
import {
  BrandControllerService,
  BrandPageResVO,
  BASE_PATH,
} from '@app/api';
import { BrandItem } from '../brand.config';
import { TranslateService } from '@ngx-translate/core';
import {
  BackendValidationErrorConfig,
  handleBackendValidationError,
} from '@app/shared/utils/error-handler.util';

export interface BrandOperationResult {
  success: boolean;
  message: string;
  type: 'create' | 'update';
}

@Injectable({
  providedIn: 'root',
})
export class BrandService {
  private translateService = inject(TranslateService);
  private httpClient = inject(HttpClient);
  private basePath = inject(BASE_PATH);
  // BehaviorSubject to communicate brand operation results
  private brandOperationResultSubject =
    new BehaviorSubject<BrandOperationResult | null>(null);
  public brandOperationResult$ =
    this.brandOperationResultSubject.asObservable();

  constructor(private brandControllerService: BrandControllerService) {}

  // Method to emit brand operation results
  emitBrandOperationResult(result: BrandOperationResult): void {
    this.brandOperationResultSubject.next(result);
  }

  // Method to clear the last result
  clearBrandOperationResult(): void {
    this.brandOperationResultSubject.next(null);
  }

  // Get brands with pagination and search
  getBrands(params: any): Observable<any> {
    const { brandName, code, status, page = 1, size = 20 } = params;

    // Convert status from boolean to number for API
    const apiStatus =
      status !== null && status !== undefined ? (status ? 1 : 0) : undefined;

    // Convert page from 1-based to 0-based for API (ensure minimum page is 0)
    const apiPage = Math.max(0, page - 1);

    // Call the real API using getList1 method
    return this.brandControllerService
      .getList1(
        undefined, // id
        brandName || undefined,
        apiStatus,
        code || undefined,
        undefined, // tenantId
        apiPage,
        size,
        undefined, // sortBy
        undefined // ascending
      )
      .pipe(
        switchMap((response: any) => {
          // Handle Blob response
          if (response instanceof Blob) {
            return from(
              new Promise<any>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  try {
                    const jsonResponse = JSON.parse(reader.result as string);

                    const brands: BrandItem[] = (
                      jsonResponse.pageContent || []
                    ).map((brand: BrandPageResVO) => {
                      return {
                        id: brand.id?.toString() || '',
                        brandName: brand.name || '',
                        code: brand.code || '',
                        logo: brand.logo || '',
                        manufacturer: brand.manufacturer || '',
                        remark: brand.remark || '',
                        status: brand.status === 1, // Convert number to boolean
                        createdName: brand.createdName || '',
                        creationDate: brand.creationDate || '',
                      };
                    });

                    resolve({
                      data: brands,
                      totalRecords: jsonResponse.totalElements || 0,
                      loading: false,
                    });
                  } catch (error) {
                    reject(error);
                  }
                };
                reader.onerror = () => reject(reader.error);
                reader.readAsText(response);
              })
            );
          }

          // Handle normal JSON response
          const brands: BrandItem[] = (response.pageContent || []).map(
            (brand: BrandPageResVO) => {
              return {
                id: brand.id?.toString() || '',
                brandName: brand.name || '',
                code: brand.code || '',
                logo: brand.logo || '',
                manufacturer: brand.manufacturer || '',
                remark: brand.remark || '',
                status: brand.status === 1, // Convert number to boolean
                createdName: brand.createdName || '',
                creationDate: brand.creationDate || '',
              };
            }
          );

          return of({
            data: brands,
            totalRecords: response.totalElements || 0,
            loading: false,
          });
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  // Get brand by ID
  getBrandById(id: string): Observable<BrandItem | null> {
    return this.brandControllerService.getOne1(parseInt(id)).pipe(
      switchMap((response: any) => {
        // Handle Blob response
        if (response instanceof Blob) {
          return from(
            new Promise<BrandItem | null>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const jsonResponse = JSON.parse(reader.result as string);

                  if (!jsonResponse) {
                    resolve(null);
                    return;
                  }

                  const mappedBrand = {
                    id: jsonResponse.id?.toString() || '',
                    brandName: jsonResponse.name || '',
                    code: jsonResponse.code || '',
                    logo: jsonResponse.logo || '',
                    manufacturer: jsonResponse.manufacturer || '',
                    remark: jsonResponse.remark || '',
                    status: jsonResponse.status === 1, // Convert number to boolean
                    createdName: jsonResponse.createdName || '',
                    creationDate: jsonResponse.creationDate || '',
                  };
                  resolve(mappedBrand);
                } catch (error) {
                  reject(error);
                }
              };
              reader.onerror = () => reject(reader.error);
              reader.readAsText(response);
            })
          );
        }

        // Handle normal JSON response
        if (!response) return of(null);

        const mappedBrand = {
          id: response.id?.toString() || '',
          brandName: response.name || '',
          code: response.code || '',
          logo: response.logo || '',
          manufacturer: response.manufacturer || '',
          remark: response.remark || '',
          status: response.status === 1, // Convert number to boolean
          createdName: response.createdName || '',
          creationDate: response.creationDate || '',
        };
        return of(mappedBrand);
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  // Create new brand using the create12 method
  createBrand(brandData: any): Observable<BrandPageResVO> {
    // Extract logo file from the form data
    let logoBlob: Blob | undefined;
    if (
      brandData.logo &&
      typeof brandData.logo === 'object' &&
      brandData.logo.file
    ) {
      logoBlob = brandData.logo.file;
    }

    // Convert status from boolean to number
    const status = brandData.status ? 1 : 0;

    // Call the create12 API method
    return this.brandControllerService
      .create12(
        brandData.code,
        brandData.brandName,
        brandData.manufacturer,
        status,
        brandData.remark,
        logoBlob,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  // Update brand using the update14 method
  updateBrand(id: string, brandData: any): Observable<BrandPageResVO> {
    // Extract logo file from the form data
    let logoBlob: Blob | undefined;

    // Check if image has changed flag is set
    if (brandData.isImageChanged === false) {
      logoBlob = undefined;
    } else if (
      brandData.isImageChanged &&
      brandData.logo &&
      typeof brandData.logo === 'object' &&
      brandData.logo.file
    ) {
      logoBlob = brandData.logo.file;
    }

    // Check if removeLogo flag is set
    const removeLogo = brandData.removeLogo === true;

    // Convert status from boolean to number
    const status = brandData.status ? 1 : 0;

    // Call the update14 API method
    return this.brandControllerService
      .update14(
        parseInt(id),
        brandData.code,
        brandData.brandName,
        brandData.manufacturer,
        status,
        brandData.remark,
        removeLogo,
        logoBlob,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }

  // Delete brand
  deleteBrand(id: string): Observable<boolean> {
    const idsReqDTO = { ids: [parseInt(id)] };
    return this.brandControllerService
      .deleteBulkByIds(idsReqDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          if (error.status === 200) {
            return of(true);
          }
          throw error;
        })
      );
  }

  // Batch delete brands
  batchDeleteBrands(ids: string[]): Observable<boolean> {
    const idsReqDTO = { ids: ids.map((id) => parseInt(id)) };
    return this.brandControllerService
      .deleteBulkByIds(idsReqDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          if (error.status === 200) {
            return of(true);
          }
          throw error;
        })
      );
  }

  // Update brand status
  updateBrandStatus(id: string, status: boolean): Observable<boolean> {
    const statusReqDTO = {
      ids: [parseInt(id)],
      status: status ? 1 : 0,
    };
    return this.httpClient
      .patch(`${this.basePath}/v1/goods/brand`, statusReqDTO, {
        responseType: 'text',
      })
      .pipe(
      map(() => true),
      catchError((error) => {
        return of(false);
      })
    );
  }

  // Batch update brand status
  batchUpdateBrandStatus(ids: string[], status: boolean): Observable<boolean> {
    // For batch status update, we'll need to call updateStatus for each ID
    // This could be optimized if the backend supports batch status update
    const statusReqDTO = {
      ids: ids.map((id) => parseInt(id)),
      status: status ? 1 : 0,
    };
    return this.httpClient
      .patch(`${this.basePath}/v1/goods/brand`, statusReqDTO, {
        responseType: 'text',
      })
      .pipe(
      map(() => true),
      catchError((error) => {
        return of(false);
      })
    );
  }

  /**
   * Handle backend validation errors and return error information
   */
  handleBackendValidationError(
    error: any,
    operation: 'create' | 'update' | 'delete' | 'batchDelete'
  ): { severity: string; summary: string; detail: string } {
    const config: BackendValidationErrorConfig = {
      errorPatterns: [
        {
          patterns: ['the same code'],
          key: 'brand.messages.error.brand_code_exists',
        },
        {
          patterns: ['has related products'],
          key: 'brand.messages.error.related_products_exist',
        },
      ],
      defaultErrorKeys: {
        create: 'brand.messages.error.failed_to_create_brand',
        update: 'brand.messages.error.failed_to_update_brand',
        delete: 'brand.messages.error.failed_to_delete_brand',
        batchDelete: 'brand.messages.error.failed_to_delete_brands',
      },
    };

    return handleBackendValidationError(
      error,
      operation,
      config,
      this.translateService
    );
  }
}
